// Lógica pura de "Traer de Mercado Libre": tipos de lo que devuelve el backend y cómo
// contarle a la persona qué pasó. Sin red ni DOM (se prueba con vitest).
// Todo Mercado Libre vive en atlas-backend (ADR-2026-09-28-mercadolibre-vive-en-el-backend).

/** Una entrada de la grilla: una publicación del modelo viejo o una familia del nuevo. */
export interface PublicacionDeMl {
  /** Qué mandar a `importar` para traer esta entrada. */
  item_ids: string[];
  titulo: string;
  miniatura: string | null;
  modelo: "viejo" | "user_products";
  es_catalogo: boolean;
  estado: string | null;
  combinaciones: number;
  colores: string[];
  precio: number | null;
  moneda: string | null;
  permalink: string | null;
  /** Si ya está en Atlas: el producto al que está vinculada. */
  producto_id: string | null;
}

export interface ListadoDeMl {
  publicaciones: PublicacionDeMl[];
  total: number;
  truncado: boolean;
}

/** Clave estable de una entrada para la selección (una familia tiene varios ids). */
export function claveDePublicacion(p: Pick<PublicacionDeMl, "item_ids">): string {
  return p.item_ids.join(",");
}

/** Lo que devuelve el job `ml_import` en su `output`. */
export interface ResultadoDelImport {
  importados?: number;
  vinculados?: number;
  ya_estaban?: number;
  ignoradas?: Array<{ item_id: string; motivo: string }>;
  fallidos?: Array<{ nombre: string; motivo: string }>;
}

function plural(n: number, uno: string, varios: string) {
  return `${n} ${n === 1 ? uno : varios}`;
}

/** "Trajimos 3 productos. 1 ya estaba en Atlas." — en palabras de la persona, sin códigos. */
export function resumenDelImport(r: ResultadoDelImport | null | undefined): { texto: string; hayProblemas: boolean } {
  if (!r) return { texto: "Listo. Los productos van a aparecer en tu catálogo en unos segundos.", hayProblemas: false };
  const partes: string[] = [];
  const importados = r.importados ?? 0;
  const vinculados = r.vinculados ?? 0;
  const yaEstaban = r.ya_estaban ?? 0;
  const fallidos = r.fallidos?.length ?? 0;
  const ignoradas = r.ignoradas?.length ?? 0;
  if (importados > 0) partes.push(`Trajimos ${plural(importados, "producto", "productos")}.`);
  if (vinculados > 0) partes.push(`Vinculamos ${plural(vinculados, "producto que ya tenías", "productos que ya tenías")}.`);
  if (yaEstaban > 0) partes.push(`${plural(yaEstaban, "ya estaba", "ya estaban")} en Atlas.`);
  if (fallidos > 0) partes.push(`${plural(fallidos, "no se pudo traer", "no se pudieron traer")}; probá de nuevo en un rato.`);
  if (ignoradas > 0 && importados + vinculados === 0 && fallidos === 0) {
    partes.push("Mercado Libre no devolvió esas publicaciones.");
  }
  if (partes.length === 0) partes.push("No había nada nuevo para traer.");
  return { texto: partes.join(" "), hayProblemas: fallidos > 0 };
}

/** Máximo de publicaciones por import (lo mismo que acepta el backend). */
export const MAX_POR_IMPORT = 500;

/** Cuántos ids suma una selección (una familia cuenta todas sus publicaciones). */
export function idsDeLaSeleccion(publicaciones: PublicacionDeMl[], elegidas: Set<string>): number {
  return publicaciones.filter((p) => elegidas.has(claveDePublicacion(p))).reduce((n, p) => n + p.item_ids.length, 0);
}
